import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getExtractedMetadata } from './LinkPrefetcher';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './pdf-viewer.css'; // Custom overrides for PDF viewer styling
import {
  Button,
  Spinner,
  Text,
  MessageBar,
  makeStyles,
  tokens,
  Tooltip,
  Badge,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Checkbox,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  DismissRegular,
  DocumentRegular,
  ErrorCircleRegular,
  ZoomInRegular,
  ZoomOutRegular,
  ResizeRegular,
  LinkRegular,
  OpenRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  EyeRegular,
} from '@fluentui/react-icons';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Document, Page, pdfjs } from 'react-pdf';
import * as mammoth from 'mammoth';
import LinkifyIt from 'linkify-it';
import { DocumentInfo, GradeData } from '../models/types';
import { ChatAPI } from '../utils/api';
import { trackException, trackEvent } from '../utils/appInsights';
import { openBlobInNewTab } from '../utils/documentWindow';
import { normalizeUiMode } from '../utils/domain';
import { ConversationSources } from './ConversationSources';
import { GradeCard } from './GradeCard';
import { ExtractedLink } from '../utils/linkExtractor';

// Configure PDF.js worker to use the local file that matches react-pdf version
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const linkify = new LinkifyIt();

interface DocumentViewerProps {
  document: DocumentInfo | null;
  authorizationId: string;
  onClose?: () => void;
  className?: string;
  onUrlClick?: (url: string) => void; // For handling URL clicks from chat
  urlToDisplay?: string; // For displaying URLs directly from chat
  conversationLinks?: ExtractedLink[]; // Links extracted from conversation
  showConversationSources?: boolean; // Whether to show the conversation sources section
  documents?: DocumentInfo[]; // All available documents for the accordion
  documentsLoading?: boolean; // Whether documents are currently loading
  documentsError?: string | null; // Any error from loading documents
  onDocumentSelect?: (document: DocumentInfo) => void; // Handler for document selection
  selectedDocuments?: string[]; // Array of selected document GUIDs from parent
  onSelectedDocumentsChange?: (documentGuids: string[]) => void; // Handler to update selected documents in parent
  gradeData?: GradeData[]; // Grade data from agent evaluations
  uiMode?: string; // Domain mode (authorization, complaint, etc.)
}

interface ViewerState {
  loading: boolean;
  error: string | null;
  content: string | null;
  contentType: 'pdf' | 'image' | 'document' | 'text' | 'html' | 'url' | 'unsupported' | null;
  blob: Blob | null;
  objectUrl: string | null;
  // PDF-specific state
  numPages: number | null;
  currentPage: number;
  pdfScale: number;
}

const useStyles = makeStyles({
  resizerActive: {
    backgroundColor: `${tokens.colorBrandBackground} !important`,
    '&::after': {
      backgroundColor: `${tokens.colorNeutralForegroundOnBrand} !important`,
      height: '6px !important', // Make the line thicker when active
    },
  },
  // Removed wrapper styles to use full blade
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
    minHeight: '300px',
    position: 'relative',
    flexGrow: 1,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    flexShrink: 0,
  },
  headerInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    flex: 1,
    minWidth: 0,
  },
  documentName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    wordBreak: 'break-word',
  },
  documentMeta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase200,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    position: 'relative',
    minHeight: '300px', // Minimum height for content area
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  errorContainer: {
    padding: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground2,
  },
  pdfContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    flex: 1,
    overflow: 'auto',
    '& .react-pdf__Document': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    '& .react-pdf__Page': {
      marginBottom: tokens.spacingVerticalM,
      boxShadow: tokens.shadow8,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    '& .react-pdf__Page__canvas': {
      maxWidth: '100%',
      height: 'auto',
    },
    '& .react-pdf__Page__textContent': {
      userSelect: 'text', // Ensure text is selectable
      '& ::selection': {
        backgroundColor: 'rgba(0, 100, 255, 0.3)',
        color: 'inherit',
      },
      '& span': {
        // Prevent size changes on highlight
        '&::selection': {
          fontSize: 'inherit !important',
          transform: 'none !important',
        }
      }
    },
  },
  pdfPage: {
    marginBottom: tokens.spacingVerticalM,
    boxShadow: tokens.shadow8,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  image: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  documentContent: {
    padding: tokens.spacingVerticalL,
    lineHeight: tokens.lineHeightBase400,
    '& p': {
      marginBottom: tokens.spacingVerticalM,
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      marginTop: tokens.spacingVerticalL,
      marginBottom: tokens.spacingVerticalM,
      fontWeight: tokens.fontWeightSemibold,
    },
    '& ul, & ol': {
      paddingLeft: tokens.spacingHorizontalL,
      marginBottom: tokens.spacingVerticalM,
    },
    '& li': {
      marginBottom: tokens.spacingVerticalS,
    },
    '& a': {
      color: tokens.colorBrandForeground1,
      textDecoration: 'underline',
      cursor: 'pointer',
      ':hover': {
        color: tokens.colorBrandForeground2,
      },
    },
  },
  textContent: {
    padding: tokens.spacingVerticalL,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    height: '100%',
    overflow: 'auto',
  },
  urlContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  urlToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  urlInput: {
    flex: 1,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    padding: tokens.spacingVerticalXS,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  iframe: {
    width: '100%',
    height: '100%',
    minHeight: '500px',
    border: 'none',
    borderRadius: tokens.borderRadiusMedium,
  },
  unsupportedContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalL,
  },
  pageNavigation: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
  },
  viewerSection: {
    flex: 3,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '180px',
    overflow: 'auto',
  },
  dragOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    cursor: 'ns-resize',
    backgroundColor: 'rgba(0, 0, 0, 0.01)', // Almost transparent but not completely
  },
  resizer: {
    height: '12px', // Increased grab area
    backgroundColor: '#f0f0f0', // Light gray background for better visibility
    cursor: 'ns-resize',
    position: 'relative',
    zIndex: 10,
    touchAction: 'none', // Prevents touch actions from interfering
    transition: 'background-color 0.1s ease',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    '&:hover': {
      backgroundColor: tokens.colorBrandBackground,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: '60px',
      height: '4px',
      backgroundColor: tokens.colorNeutralStroke1,
      borderRadius: '2px',
      transition: 'background-color 0.1s ease',
    },
  },
  accordionSection: {
    overflow: 'auto',
    flex: 2,
    minHeight: '120px',
    maxHeight: '55%',
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
  },
  accordionHeader: {
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  accordionPanel: {
    maxHeight: '250px', // Limit individual panel height
    overflow: 'auto', // Enable scrolling for panel content
    padding: tokens.spacingVerticalXS,
  },
  documentItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingVerticalXS,
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusMedium,
    transition: 'background-color 0.2s ease-in-out',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  selectedDocument: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
  },
  accordionDocumentName: {
    fontWeight: tokens.fontWeightMedium,
    wordBreak: 'break-word',
  },
  accordionDocumentMeta: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
  },
});

// Memoized PDF component to prevent re-renders when conversation links change
const PdfViewer = React.memo(({
  objectUrl,
  currentPage,
  pdfScale,
  onDocumentLoadSuccess,
  onDocumentLoadError,
}: {
  objectUrl: string | null;
  currentPage: number;
  pdfScale: number;
  onDocumentLoadSuccess: (pdf: any) => void;
  onDocumentLoadError: (error: Error) => void;
}) => {
  return (
    <Document
      file={objectUrl}
      onLoadSuccess={onDocumentLoadSuccess}
      onLoadError={onDocumentLoadError}
      loading={<Spinner size="medium" />}
      error={
        <div style={{ textAlign: 'center', padding: tokens.spacingVerticalL }}>
          <Text>Failed to load PDF</Text>
        </div>
      }
    >
      <Page
        pageNumber={currentPage}
        scale={pdfScale}
        loading={<Spinner size="small" />}
        error={
          <div style={{ textAlign: 'center', padding: tokens.spacingVerticalM }}>
            <Text>Failed to load page</Text>
          </div>
        }
        className="pdf-viewer-page"
        renderTextLayer={true}
        renderAnnotationLayer={true}
        // Do not set customTextRenderer to avoid layer duplication
      />
    </Document>
  );
});

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document,
  authorizationId,
  onClose,
  className,
  onUrlClick,
  urlToDisplay,
  conversationLinks = [],
  showConversationSources = true,
  documents = [],
  documentsLoading = false,
  documentsError = null,
  onDocumentSelect,
  selectedDocuments = [],
  onSelectedDocumentsChange,
  gradeData = [],
  uiMode = 'authorization',
}) => {
  // Simple state to hold current URL metadata
  const [urlMetadata, setUrlMetadata] = useState<{title?: string; description?: string}>({});
  const [openingDocuments, setOpeningDocuments] = useState<Set<string>>(new Set());

  // Handle select all
  const handleSelectAll = () => {
    if (!onSelectedDocumentsChange) return;
    
    if (selectedDocuments.length === documents.length && documents.length > 0) {
      // Deselect all
      onSelectedDocumentsChange([]);
    } else {
      // Select all
      onSelectedDocumentsChange(documents.map(doc => doc.document_guid));
    }
  };

  // Handle individual document selection
  const handleDocumentToggle = (documentGuid: string) => {
    if (!onSelectedDocumentsChange) return;
    
    if (selectedDocuments.includes(documentGuid)) {
      // Remove from selection
      onSelectedDocumentsChange(selectedDocuments.filter(id => id !== documentGuid));
    } else {
      // Add to selection
      onSelectedDocumentsChange([...selectedDocuments, documentGuid]);
    }
  };

  const handleOpenInNewTab = async (doc: DocumentInfo) => {
    const documentTab = window.open('', '_blank');
    if (!documentTab) {
      alert('Allow pop-ups to open this document in a new tab.');
      return;
    }

    documentTab.opener = null;
    setOpeningDocuments(prev => new Set(prev).add(doc.document_guid));

    try {
      const normalizedUiMode = normalizeUiMode(uiMode);
      const blob = doc.document_type === 'Aurora Summary'
        ? normalizedUiMode === 'complaint'
          ? await ChatAPI.getCcrSummaryPdf(
            doc.document_guid,
            doc.document_created_on,
            authorizationId
          )
          : await ChatAPI.getAuthorizationSummaryPdf(
            authorizationId,
            doc.document_created_on
          )
        : await ChatAPI.downloadDocument(
          authorizationId,
          doc.document_guid,
          doc.document_name,
          normalizedUiMode
        );

      if (documentTab.closed) return;
      openBlobInNewTab(documentTab, blob);
    } catch (err) {
      documentTab.close();
      const errorMessage = err instanceof Error ? err.message : 'Failed to open document';
      trackException(new Error(`Document open failed: ${errorMessage}`), {
        authorizationId,
        documentGuid: doc.document_guid,
        errorType: 'document_open_error',
      });
      alert(`Failed to open document: ${errorMessage}`);
    } finally {
      setOpeningDocuments(prev => {
        const next = new Set(prev);
        next.delete(doc.document_guid);
        return next;
      });
    }
  };
  
  // Update metadata when URL changes or when new metadata becomes available
  useEffect(() => {
    if (!urlToDisplay) {
      setUrlMetadata({});
      return;
    }
    
    // Get current metadata
    const currentMetadata = getExtractedMetadata(urlToDisplay);
    setUrlMetadata(currentMetadata);
    
    // Set up a simple interval to check for updated metadata
    const interval = setInterval(() => {
      const updatedMetadata = getExtractedMetadata(urlToDisplay);
      if (updatedMetadata.title !== currentMetadata.title || updatedMetadata.description !== currentMetadata.description) {
        console.debug('DocumentViewer: Found updated metadata for', urlToDisplay, updatedMetadata);
        setUrlMetadata(updatedMetadata);
      }
    }, 1000); // Check every second
    
    return () => clearInterval(interval);
  }, [urlToDisplay]);
  
  // State for resizable sections
  const [accordionHeight, setAccordionHeight] = useState<number>(300);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  
  // Refs for stable references that don't trigger re-renders
  const containerRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);
  const lastAccordionHeightRef = useRef(300);
  
  // Update ref when state changes
  useEffect(() => {
    isResizingRef.current = isResizing;
  }, [isResizing]);
  
  useEffect(() => {
    lastAccordionHeightRef.current = accordionHeight;
  }, [accordionHeight]);
  
  // Define handlers directly to avoid ref issues
  const handleMouseMove = useCallback((e: MouseEvent) => {
    // Check if we're resizing
    if (!isResizingRef.current || !containerRef.current) return;
    
    // Calculate directly for immediate feedback
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerHeight = containerRect.height;
    const mouseY = e.clientY - containerRect.top;
    
    // Calculate new accordion height (inverse of mouse position)
    let newAccordionHeight = containerHeight - mouseY;
    
    // Set permissive limits
    const minHeight = 50;
    const maxHeight = containerHeight - 100;
    
    // Apply limits
    newAccordionHeight = Math.min(Math.max(minHeight, newAccordionHeight), maxHeight);
    
    // Update height immediately
    setAccordionHeight(newAccordionHeight);
  }, []);
  
  // Handle mouse up to end resizing
  const handleMouseUp = useCallback(() => {
    // Only process if we were resizing
    if (!isResizingRef.current) return;
    
    // Update state
    isResizingRef.current = false;
    setIsResizing(false);
    
    // Clean up event listeners
    window.document.removeEventListener('mousemove', handleMouseMove);
    window.document.removeEventListener('mouseup', handleMouseUp);
    
    // Reset cursor and user selection
    window.document.body.style.cursor = '';
    window.document.body.style.userSelect = '' as any;
    
    // Track resize event
    trackEvent('DocumentViewerResized', {
      authorizationId,
      documentGuid: document?.document_guid,
      accordionHeight: lastAccordionHeightRef.current,
      timestamp: new Date().toISOString(),
    });
  }, [authorizationId, document]);
  
  // Clean up event listeners on unmount
  useEffect(() => {
    return () => {
      // Clean up event listeners if we're still resizing
      if (isResizingRef.current) {
        window.document.removeEventListener('mousemove', handleMouseMove);
        window.document.removeEventListener('mouseup', handleMouseUp);
        
        // Reset body styles
        window.document.body.style.cursor = '';
        window.document.body.style.userSelect = '' as any;
        isResizingRef.current = false;
      }
    };
  }, [handleMouseMove, handleMouseUp]);
  
  // Handle mouse down on resizer
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Use ref directly to avoid state updates during drag operation
    isResizingRef.current = true;
    setIsResizing(true);
    lastAccordionHeightRef.current = accordionHeight;
    
    // Attach event listeners
    window.document.addEventListener('mousemove', handleMouseMove);
    window.document.addEventListener('mouseup', handleMouseUp);
    
    // Add cursor style to body while resizing
    window.document.body.style.cursor = 'ns-resize';
    // Prevent text selection during resize
    window.document.body.style.userSelect = 'none' as any;
  }, [handleMouseMove, handleMouseUp, accordionHeight]);

  const [viewerState, setViewerState] = useState<ViewerState>({
    loading: false,
    error: null,
    content: null,
    contentType: null,
    blob: null,
    objectUrl: null,
    numPages: null,
    currentPage: 1,
    pdfScale: 1.75,
  });
  const loadGenerationRef = useRef(0);
  

  
  const styles = useStyles();



  // Clean up object URL when component unmounts or document changes
  useEffect(() => {
    return () => {
      if (viewerState.objectUrl) {
        URL.revokeObjectURL(viewerState.objectUrl);
      }
    };
  }, [viewerState.objectUrl]);

  // Handle URL display from chat
  useEffect(() => {
    if (urlToDisplay && !document) {
      setViewerState({
        loading: false,
        error: null,
        content: urlToDisplay,
        contentType: 'url',
        blob: null,
        objectUrl: null,
        numPages: null,
        currentPage: 1,
        pdfScale: 1.75,
      });
    }
  }, [urlToDisplay, document]);

  const loadDocument = useCallback(async () => {
    if (!document || !authorizationId) return;

    const loadGeneration = ++loadGenerationRef.current;
    let objectUrl: string | null = null;

    setViewerState({
      loading: true,
      error: null,
      content: null,
      contentType: null,
      blob: null,
      objectUrl: null,
      numPages: null,
      currentPage: 1,
      pdfScale: 1.75,
    });

    try {
      trackEvent('DocumentViewerLoadRequested', {
        authorizationId,
        documentGuid: document.document_guid,
        documentName: document.document_name,
        fileExtension: document.file_extension,
        timestamp: new Date().toISOString(),
      });

      const normalizedUiMode = normalizeUiMode(uiMode);
      const blob = document.document_type === 'Aurora Summary'
        ? normalizedUiMode === 'complaint'
          ? await ChatAPI.getCcrSummaryPdf(
            document.document_guid,
            document.document_created_on,
            authorizationId
          )
          : await ChatAPI.getAuthorizationSummaryPdf(
            authorizationId,
            document.document_created_on
          )
        : await ChatAPI.downloadDocument(
          authorizationId,
          document.document_guid,
          document.document_name,
          normalizedUiMode
        );

      if (loadGeneration !== loadGenerationRef.current) return;

      objectUrl = URL.createObjectURL(blob);
      const extension = document.file_extension.toLowerCase();
      let contentType: ViewerState['contentType'] = 'unsupported';
      let content: string | null = null;

      // Determine content type and process accordingly
      if (extension === '.pdf') {
        contentType = 'pdf';
      } else if (['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'].includes(extension)) {
        contentType = 'image';
      } else if (['.docx', '.doc'].includes(extension)) {
        contentType = 'document';
        try {
          const arrayBuffer = await blob.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          content = result.value;
          
          // Make links clickable but not open in new tab
          content = content.replace(
            /<a href="([^"]*)"[^>]*>/g,
            '<a href="#" onclick="event.preventDefault(); window.parent.handleDocumentLink(\'$1\');" style="color: var(--colorBrandForeground1); text-decoration: underline; cursor: pointer;">'
          );
        } catch (err) {
          console.warn('Failed to parse document with mammoth, treating as unsupported:', err);
          contentType = 'unsupported';
        }
      } else if (['.txt', '.csv', '.json', '.xml', '.md'].includes(extension)) {
        contentType = 'text';
        content = await blob.text();
        
        // Linkify URLs in text content
        const matches = linkify.match(content);
        if (matches) {
          let linkifiedContent = content;
          matches.reverse().forEach(match => {
            linkifiedContent = 
              linkifiedContent.slice(0, match.index) +
              `<a href="#" onclick="event.preventDefault(); window.parent.handleDocumentLink('${match.url}');" style="color: var(--colorBrandForeground1); text-decoration: underline; cursor: pointer;">${match.text}</a>` +
              linkifiedContent.slice(match.lastIndex);
          });
          content = linkifiedContent;
        }
      } else if (['.html', '.htm'].includes(extension)) {
        contentType = 'html';
        content = await blob.text();
      }

      if (loadGeneration !== loadGenerationRef.current) {
        URL.revokeObjectURL(objectUrl);
        return;
      }

      setViewerState({
        loading: false,
        error: null,
        content,
        contentType,
        blob,
        objectUrl,
        numPages: null,
        currentPage: 1,
        pdfScale: 1.75,
      });
      objectUrl = null;

      trackEvent('DocumentViewerLoadCompleted', {
        authorizationId,
        documentGuid: document.document_guid,
        contentType,
        timestamp: new Date().toISOString(),
      });

    } catch (err) {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      if (loadGeneration !== loadGenerationRef.current) return;

      const errorMessage = err instanceof Error ? err.message : 'Failed to load document';
      setViewerState({
        loading: false,
        error: errorMessage,
        content: null,
        contentType: null,
        blob: null,
        objectUrl: null,
        numPages: null,
        currentPage: 1,
        pdfScale: 1.75,
      });

      trackException(new Error(`Document viewer load failed: ${errorMessage}`), {
        authorizationId,
        documentGuid: document?.document_guid,
        errorType: 'document_viewer_load_error',
      });
    }
  }, [document, authorizationId, uiMode]);

  useEffect(() => {
    if (document && !urlToDisplay) {
      void loadDocument();
    } else if (!document && !urlToDisplay) {
      setViewerState({
        loading: false,
        error: null,
        content: null,
        contentType: null,
        blob: null,
        objectUrl: null,
        numPages: null,
        currentPage: 1,
        pdfScale: 1.75,
      });
    }
    return () => {
      loadGenerationRef.current += 1;
    };
  }, [document, loadDocument, urlToDisplay]);

  // Expose link handler to window for document links
  useEffect(() => {
    (window as any).handleDocumentLink = (url: string) => {
      if (onUrlClick) {
        onUrlClick(url);
      } else {
        // Fallback to displaying URL in viewer
        setViewerState(prev => ({
          ...prev,
          content: url,
          contentType: 'url',
        }));
      }
    };

    return () => {
      delete (window as any).handleDocumentLink;
    };
  }, [onUrlClick]);

  const handleDownload = async () => {
    if (!document || !viewerState.blob) return;

    try {
      const url = URL.createObjectURL(viewerState.blob);
      const link = window.document.createElement('a');
      link.href = url;
      
      const filename = `${authorizationId}_${document.document_guid}${document.file_extension}`;
      link.download = filename;
      
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);

      trackEvent('DocumentViewerDownloadCompleted', {
        authorizationId,
        documentGuid: document.document_guid,
        filename,
        timestamp: new Date().toISOString(),
      });

    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setViewerState(prev => ({ ...prev, numPages, currentPage: 1 }));
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    
    // If it's a worker-related error, fall back to download option
    if (error.message.includes('worker') || error.message.includes('Worker')) {
      setViewerState(prev => ({ 
        ...prev, 
        error: 'PDF preview requires additional setup. Please download the file to view it.',
        contentType: 'unsupported'
      }));
    } else {
      setViewerState(prev => ({ ...prev, error: 'Failed to load PDF document' }));
    }
  };

  const goToPrevPage = () => {
    setViewerState(prev => ({
      ...prev,
      currentPage: Math.max(1, prev.currentPage - 1)
    }));
  };

  const goToNextPage = () => {
    setViewerState(prev => ({
      ...prev,
      currentPage: Math.min(prev.numPages || 1, prev.currentPage + 1)
    }));
  };

  const zoomIn = () => {
    setViewerState(prev => ({
      ...prev,
      pdfScale: Math.min(3.0, prev.pdfScale + 0.25)
    }));
  };

  const zoomOut = () => {
    setViewerState(prev => ({
      ...prev,
      pdfScale: Math.max(0.5, prev.pdfScale - 0.25)
    }));
  };

  const resetZoom = () => {
    setViewerState(prev => ({
      ...prev,
      pdfScale: 1.75
    }));
  };

  // Handle clicks on links in document content
  const handleContentClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A') {
      event.preventDefault();
      const href = target.getAttribute('href');
      if (href) {
        window.open(href, '_blank', 'noopener,noreferrer');
        
        trackEvent('DocumentViewerLinkClicked', {
          authorizationId,
          documentGuid: document?.document_guid,
          linkUrl: href,
          timestamp: new Date().toISOString(),
        });
      }
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const renderDocumentItem = (doc: DocumentInfo) => {
    const isViewing = document?.document_guid === doc.document_guid;
    const isSelected = selectedDocuments.includes(doc.document_guid);
    
    return (
      <div
        key={doc.document_guid}
        className={`${styles.documentItem} ${isViewing ? styles.selectedDocument : ''}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.spacingHorizontalS,
          padding: tokens.spacingVerticalXS,
        }}
      >
        {/* Checkbox */}
        <Checkbox
          checked={isSelected}
          onChange={() => handleDocumentToggle(doc.document_guid)}
          aria-label={`Select ${doc.document_name}`}
        />

        {/* Document Name and Meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={styles.accordionDocumentName}>{doc.document_name}</div>
          <div className={styles.accordionDocumentMeta}>
            {formatDate(doc.document_created_on)}
            {doc.processing_stage && (
              <>
                {' • '}
                <Badge appearance="tint" size="small">
                  {doc.processing_stage}
                </Badge>
              </>
            )}
          </div>
        </div>

        {/* View and open actions */}
        {isViewing ? (
          <Text
            size={300}
            weight="semibold"
            style={{ 
              color: tokens.colorBrandForeground1,
              whiteSpace: 'nowrap',
            }}
          >
            Viewing
          </Text>
        ) : (
          <Button
            appearance="subtle"
            size="small"
            icon={<EyeRegular />}
            onClick={() => onDocumentSelect && onDocumentSelect(doc)}
            title="View document"
          >
            View
          </Button>
        )}
        <Tooltip content="Open in new tab" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={openingDocuments.has(doc.document_guid) ? <Spinner size="tiny" /> : <OpenRegular />}
            onClick={(event) => {
              event.stopPropagation();
              void handleOpenInNewTab(doc);
            }}
            disabled={openingDocuments.has(doc.document_guid)}
            aria-label={`Open ${doc.document_name} in a new tab`}
          />
        </Tooltip>
      </div>
    );
  };

  // Commented out as it's currently unused - can be re-enabled if needed in the future
  /*
  const linkifyContent = (content: string | null): string => {
    if (!content) return '';
    
    try {
      const matches = linkify.match(content);
      if (!matches || matches.length === 0) {
        return content;
      }

      let linkifiedContent = content;
      // Process matches in reverse order to avoid index shifting
      for (let i = matches.length - 1; i >= 0; i--) {
        const match = matches[i];
        if (linkifiedContent) { // Add null check
          linkifiedContent = 
            linkifiedContent.slice(0, match.index) +
            `<a href="${match.url}" target="_blank" rel="noopener noreferrer">${match.text}</a>` +
            linkifiedContent.slice(match.lastIndex);
        }
      }
      
      return linkifiedContent || content;
    } catch (error) {
      console.error('Error linkifying content:', error);
      return content;
    }
  };
  */

  const renderContent = () => {
    if (viewerState.loading) {
      return (
        <div className={styles.loadingContainer}>
          <Spinner size="large" />
          <Text>Loading document...</Text>
        </div>
      );
    }

    if (viewerState.error) {
      return (
        <div className={styles.errorContainer}>
          <ErrorCircleRegular style={{ fontSize: '48px', color: tokens.colorPaletteRedForeground1 }} />
          <MessageBar intent="error">
            {viewerState.error}
          </MessageBar>
          <Button onClick={loadDocument} appearance="primary" size="small">
            Retry
          </Button>
        </div>
      );
    }

    if (!viewerState.contentType) {
      return (
        <div className={styles.emptyContainer}>
          <DocumentRegular style={{ fontSize: '48px', color: tokens.colorNeutralForeground3 }} />
          <Text size={400}>No Document Selected</Text>
          <Text size={300}>Click on a document from the list below to view it here</Text>
        </div>
      );
    }

    switch (viewerState.contentType) {
      case 'pdf':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className={styles.toolbar}>
              <Tooltip content="Zoom out" relationship="label">
                <Button
                  icon={<ZoomOutRegular />}
                  size="small"
                  onClick={zoomOut}
                  disabled={viewerState.pdfScale <= 0.5}
                />
              </Tooltip>
              <Text size={200}>{Math.round(viewerState.pdfScale * 100)}%</Text>
              <Tooltip content="Zoom in" relationship="label">
                <Button
                  icon={<ZoomInRegular />}
                  size="small"
                  onClick={zoomIn}
                  disabled={viewerState.pdfScale >= 3.0}
                />
              </Tooltip>
              <Tooltip content="Reset zoom" relationship="label">
                <Button
                  icon={<ResizeRegular />}
                  size="small"
                  onClick={resetZoom}
                />
              </Tooltip>
              {viewerState.numPages && (
                <>
                  <div style={{ borderLeft: `1px solid ${tokens.colorNeutralStroke1}`, height: '20px', margin: `0 ${tokens.spacingHorizontalS}` }} />
                  <Tooltip content="Previous page" relationship="label">
                    <Button
                      icon={<ChevronLeftRegular />}
                      size="small"
                      onClick={goToPrevPage}
                      disabled={viewerState.currentPage <= 1}
                    />
                  </Tooltip>
                  <Text size={200}>
                    Page {viewerState.currentPage} of {viewerState.numPages}
                  </Text>
                  <Tooltip content="Next page" relationship="label">
                    <Button
                      icon={<ChevronRightRegular />}
                      size="small"
                      onClick={goToNextPage}
                      disabled={viewerState.currentPage >= viewerState.numPages}
                    />
                  </Tooltip>
                </>
              )}
            </div>
            <div className={styles.pdfContainer}>
              <PdfViewer
                objectUrl={viewerState.objectUrl}
                currentPage={viewerState.currentPage}
                pdfScale={viewerState.pdfScale}
                onDocumentLoadSuccess={onDocumentLoadSuccess}
                onDocumentLoadError={onDocumentLoadError}
              />
            </div>
          </div>
        );

      case 'image':
        return (
          <div className={styles.imageContainer}>
            <TransformWrapper
              initialScale={1}
              minScale={0.1}
              maxScale={5}
              centerOnInit
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <>
                  <div className={styles.toolbar}>
                    <Tooltip content="Zoom in" relationship="label">
                      <Button
                        icon={<ZoomInRegular />}
                        size="small"
                        onClick={() => zoomIn()}
                      />
                    </Tooltip>
                    <Tooltip content="Zoom out" relationship="label">
                      <Button
                        icon={<ZoomOutRegular />}
                        size="small"
                        onClick={() => zoomOut()}
                      />
                    </Tooltip>
                    <Tooltip content="Reset zoom" relationship="label">
                      <Button
                        icon={<ResizeRegular />}
                        size="small"
                        onClick={() => resetTransform()}
                      />
                    </Tooltip>
                  </div>
                  <TransformComponent
                    wrapperStyle={{
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <img
                      src={viewerState.objectUrl || ''}
                      alt={document?.document_name || 'Document'}
                      className={styles.image}
                      onError={() => setViewerState(prev => ({ ...prev, error: 'Failed to load image' }))}
                    />
                  </TransformComponent>
                </>
              )}
            </TransformWrapper>
          </div>
        );

      case 'document':
        return (
          <div
            className={styles.documentContent}
            dangerouslySetInnerHTML={{ __html: viewerState.content || '' }}
            onClick={handleContentClick}
          />
        );

      case 'text':
        return (
          <div
            className={styles.textContent}
            dangerouslySetInnerHTML={{ __html: viewerState.content || '' }}
          />
        );

      case 'html':
        return (
          <div
            className={styles.documentContent}
            dangerouslySetInnerHTML={{ __html: viewerState.content || '' }}
            onClick={handleContentClick}
          />
        );

      case 'url':
        return (
          <div className={styles.urlContainer}>
            <div className={styles.urlToolbar}>
              <LinkRegular />
              <input
                type="text"
                value={viewerState.content || ''}
                readOnly
                className={styles.urlInput}
              />
              <Tooltip content="Open in new tab" relationship="label">
                <Button
                  size="small"
                  icon={<OpenRegular />}
                  onClick={() => window.open(viewerState.content || '', '_blank', 'noopener,noreferrer')}
                />
              </Tooltip>
            </div>
            {/* URL title extraction for LCD/NCD documents */}
            {viewerState.content && (viewerState.content.includes('lcd.aspx') || viewerState.content.includes('ncd.aspx')) && (
              <div style={{ 
                padding: tokens.spacingVerticalS,
                backgroundColor: tokens.colorNeutralBackground2,
                borderBottom: `1px solid ${tokens.colorNeutralStroke1}`
              }}>
                <Text weight="semibold">
                  {viewerState.content.includes('lcd.aspx') ? 'Local Coverage Determination (LCD)' : 'National Coverage Determination (NCD)'}
                </Text>
                <Text size={200}>
                  {viewerState.content.includes('lcd.aspx') 
                    ? 'Medicare coverage policy document providing guidance for specific procedures or medical equipment.'
                    : 'Medicare coverage policy document setting national standards for covered procedures or equipment.'}
                </Text>
              </div>
            )}
            <iframe
              src={viewerState.content || ''}
              className={styles.iframe}
              title="Web Content"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              onError={(e) => {
                console.warn('Frame loading error:', e);
              }}
            />
            {/* Fallback link in case iframe fails to load due to CSP restrictions */}
            <div style={{
              padding: tokens.spacingVerticalS,
              textAlign: 'center',
              borderTop: `1px solid ${tokens.colorNeutralStroke2}`
            }}>
              <Text size={200}>If the content doesn't load correctly, you can </Text>
              <Button 
                appearance="transparent" 
                onClick={() => window.open(viewerState.content || '', '_blank', 'noopener,noreferrer')}
              >
                open in a new tab
              </Button>
            </div>
          </div>
        );

      case 'unsupported':
      default:
        return (
          <div className={styles.unsupportedContainer}>
            <DocumentRegular style={{ fontSize: '48px', color: tokens.colorNeutralForeground3 }} />
            <Text size={400}>Preview not available</Text>
            <Text size={300} style={{ color: tokens.colorNeutralForeground2, textAlign: 'center' }}>
              This file type is not supported for preview.{' '}
              <br />
              You can download the file to view it in an external application.
            </Text>
            <Button
              icon={<ArrowDownloadRegular />}
              onClick={handleDownload}
              appearance="primary"
            >
              Download File
            </Button>
          </div>
        );
    }
  };

  // Display URL content if provided from chat
  if (urlToDisplay && !document) {
    // Determine document type
    const docType = urlToDisplay.includes('lcd.aspx') ? 'LCD' : 
                   urlToDisplay.includes('ncd.aspx') ? 'NCD' : undefined;
    
    // Use the state-based metadata (React will automatically re-render when this changes)
    const medicareInfo = {
      title: urlMetadata.title || 
             (docType ? `${docType} Document` : 'Web Content'),
      description: urlMetadata.description || 
                  (docType === 'LCD' ? 'Local Coverage Determination - Medicare coverage policy' :
                   docType === 'NCD' ? 'National Coverage Determination - Medicare coverage policy' :
                   `URL: ${urlToDisplay}`),
      type: docType
    };
    
    console.debug('DocumentViewer render - using state metadata:', urlMetadata, 'final info:', medicareInfo);
    
    return (
      <div className={`${styles.container} ${className || ''}`} ref={containerRef}>
        <div className={styles.viewerSection}>
          <div className={styles.header}>
            <div className={styles.headerInfo}>
              <div className={styles.documentName}>
                {medicareInfo.title}
              </div>
              <div className={styles.documentMeta}>
                {medicareInfo.description}
              </div>
            </div>
            <div className={styles.headerActions}>
              <Tooltip content="Open in browser" relationship="label">
                <Button
                  icon={<OpenRegular />}
                  onClick={() => window.open(urlToDisplay, '_blank', 'noopener,noreferrer')}
                  appearance="subtle"
                  size="small"
                />
              </Tooltip>
              {onClose && (
                <Tooltip content="Close viewer" relationship="label">
                  <Button
                    icon={<DismissRegular />}
                    onClick={onClose}
                    appearance="secondary"
                    size="small"
                  />
                </Tooltip>
              )}
            </div>
          </div>
          <div className={styles.content}>
            {/* Display metadata panel for Medicare documents */}
            {medicareInfo && (
              <div style={{ 
                padding: tokens.spacingVerticalM,
                backgroundColor: tokens.colorNeutralBackground2,
                borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.spacingVerticalS
              }}>
                <Badge appearance="filled" color="brand">
                  {medicareInfo.type === 'LCD' ? 'Local Coverage Determination' : 'National Coverage Determination'}
                </Badge>
                <Text size={400} weight="semibold">
                  {medicareInfo.title || (medicareInfo.type ? `${medicareInfo.type} Document` : 'Medicare Document')}
                </Text>
                <Text>
                  {medicareInfo.description ||
                   (medicareInfo.type === 'LCD' 
                    ? 'This document provides specific regional coverage guidance for the requested procedure or equipment.'
                    : 'This document provides national coverage standards and requirements for the requested procedure or equipment.')}
                </Text>
              </div>
            )}
            <div className={styles.urlContainer}>
              {/* Display any extracted metadata that wasn't used in the header */}
              {urlMetadata.description && urlMetadata.description !== medicareInfo?.description && (
                <div style={{ 
                  padding: tokens.spacingVerticalS,
                  backgroundColor: tokens.colorNeutralBackground2,
                  borderBottom: `1px solid ${tokens.colorNeutralStroke1}`
                }}>
                  <Text size={200} weight="medium">
                    {urlMetadata.description}
                  </Text>
                </div>
              )}
              <iframe
                src={urlToDisplay}
                className={styles.iframe}
                title={medicareInfo.title || "Web Content"}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                onError={(e) => {
                  console.warn('Frame loading error:', e);
                }}
              />
              {/* Fallback link in case iframe fails to load due to CSP restrictions */}
              <div style={{
                padding: tokens.spacingVerticalS,
                textAlign: 'center',
                borderTop: `1px solid ${tokens.colorNeutralStroke2}`
              }}>
                <Text size={200}>If the content doesn't load correctly, you can </Text>
                <Button 
                  appearance="transparent" 
                  onClick={() => window.open(urlToDisplay, '_blank', 'noopener,noreferrer')}
                >
                  open in a new tab
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Empty accordion section for consistency with document view */}
        <div className={styles.resizer} aria-hidden="true" />
        <div className={styles.accordionSection} style={{ height: `${accordionHeight}px` }}>
          <div style={{ padding: tokens.spacingVerticalM, textAlign: 'center' }}>
            <Text>No additional documents available</Text>
          </div>
        </div>
      </div>
    );
  }

  // Always render the full component including accordions, even if no document selected
  // Removed early return to ensure accordion always renders

  return (
    <div className={`${styles.container} ${className || ''}`} ref={containerRef}>
      {/* Document Viewer Section - Always at top */}
      
      <div className={styles.viewerSection}>
        <div className={styles.header}>
        <div className={styles.headerInfo}>
          <div className={styles.documentName}>
            {document?.document_name || 'Document Viewer'}
          </div>
          <div className={styles.documentMeta}>
            {document ? (
              <>
                {formatDate(document.document_created_on)}
                {document.processing_stage && (
                  <>
                    {' • '}
                    <Badge appearance="tint" size="small">
                      {document.processing_stage}
                    </Badge>
                  </>
                )}
              </>
            ) : 'No document selected'}
          </div>
        </div>
        <div className={styles.headerActions}>
          {viewerState.blob && (
            <Tooltip content="Download document" relationship="label">
              <Button
                icon={<ArrowDownloadRegular />}
                onClick={handleDownload}
                appearance="secondary"
                size="small"
              />
            </Tooltip>
          )}
          {onClose && (
            <Tooltip content="Close viewer" relationship="label">
              <Button
                icon={<DismissRegular />}
                onClick={onClose}
                appearance="secondary"
                size="small"
              />
            </Tooltip>
          )}
        </div>
      </div>
      
      <div className={styles.content}>
        {renderContent()}
      </div>
    </div>

      {/* Resizer element */}
      <div 
        className={`${styles.resizer} ${isResizing ? styles.resizerActive : ''}`} 
        onMouseDown={handleMouseDown}
        aria-hidden="true"
        title="Drag to resize sections"
      />    {/* Accordion Sections Below */}
    <div className={styles.accordionSection} style={{ height: `${accordionHeight}px` }}>
        <Accordion multiple={true} collapsible={true} defaultOpenItems={["documents"]}>
          {/* Documents Section - Show when documents are loaded */}
          <AccordionItem value="documents">
            <AccordionHeader className={styles.accordionHeader}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                width: '100%',
                paddingRight: tokens.spacingHorizontalS,
              }}>
                <Text weight="semibold">
                  Documents ({documents.length})
                </Text>
                {documents.length > 0 && (
                  <Button
                    appearance="subtle"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent accordion toggle
                      handleSelectAll();
                    }}
                  >
                    {selectedDocuments.length === documents.length && documents.length > 0
                      ? 'Deselect All'
                      : 'Select All'}
                  </Button>
                )}
              </div>
            </AccordionHeader>
            <AccordionPanel className={styles.accordionPanel}>
              {documents.length > 0 ? (
                documents.map(renderDocumentItem)
              ) : documentsLoading ? (
                <div style={{ padding: tokens.spacingVerticalM, textAlign: 'center', color: tokens.colorNeutralForeground2 }}>
                  <Spinner size="medium" />
                  <Text style={{ marginTop: tokens.spacingVerticalS }}>Loading documents...</Text>
                </div>
              ) : documentsError ? (
                <div style={{ padding: tokens.spacingVerticalM, textAlign: 'center', color: tokens.colorPaletteRedForeground1 }}>
                  <ErrorCircleRegular style={{ fontSize: '24px', marginBottom: tokens.spacingVerticalS }} />
                  <Text>Failed to load documents</Text>
                  <Text size={200} style={{ marginTop: tokens.spacingVerticalXS }}>
                    {documentsError}
                  </Text>
                </div>
              ) : (
                <div style={{ padding: tokens.spacingVerticalM, textAlign: 'center', color: tokens.colorNeutralForeground2 }}>
                  <DocumentRegular style={{ fontSize: '24px', marginBottom: tokens.spacingVerticalS }} />
                  <Text>No documents found</Text>
                  <Text size={200} style={{ marginTop: tokens.spacingVerticalXS }}>
                    {authorizationId ? 'This authorization has no associated documents' : 'Enter an authorization number to search for documents'}
                  </Text>
                </div>
              )}
            </AccordionPanel>
          </AccordionItem>

          {/* Conversation Sources Section - Only show if we have messages AND sources */}
          {showConversationSources && (
            <AccordionItem value="sources">
              <AccordionHeader className={styles.accordionHeader}>
                <Text weight="semibold">
                  Conversation Sources ({conversationLinks.length})
                </Text>
              </AccordionHeader>
              <AccordionPanel className={styles.accordionPanel}>
                <ConversationSources
                  links={conversationLinks}
                  onLinkClick={(link) => {
                    if (onUrlClick) {
                      onUrlClick(link.url);
                    }
                  }}
                />
              </AccordionPanel>
            </AccordionItem>
          )}

          {/* Response Grading Section */}
          <AccordionItem value="grading">
            <AccordionHeader className={styles.accordionHeader}>
              <Text weight="semibold">
                Response Grading ({gradeData.length})
              </Text>
            </AccordionHeader>
            <AccordionPanel className={styles.accordionPanel}>
              {gradeData.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                  {gradeData.map((grade, index) => (
                    <GradeCard
                      key={`grade-${grade.timestamp}-${index}`}
                      grade={grade}
                      index={index}
                    />
                  ))}
                </div>
              ) : (
                <div style={{ 
                  padding: tokens.spacingVerticalL,
                  textAlign: 'center',
                  color: tokens.colorNeutralForeground2
                }}>
                  <Text>No response grading data available</Text>
                  <Text size={200} style={{ marginTop: tokens.spacingVerticalXS, display: 'block' }}>
                    Response quality evaluations will appear here after AI interactions
                  </Text>
                </div>
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};

// Keep only one export for the component
export default DocumentViewer;